import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { FiMail, FiLock, FiUser, FiArrowLeft, FiZap } from 'react-icons/fi';
import { FaApple } from 'react-icons/fa';

export default function AuthScreen({ onSkip, initialMode = 'login' }) {
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [confirmCode, setConfirmCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { signIn, signUp, confirmAccount, signInWithApple, error, clearError } = useAuth();

  async function handleAppleSignIn() {
    if (submitting) return;
    setSubmitting(true);
    try {
      await signInWithApple();
    } catch {
      // error is set via context
    }
    setSubmitting(false);
  }

  async function handleLogin(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await signIn(email, password);
    } catch {
      // error is set via context
    }
    setSubmitting(false);
  }

  async function handleSignUp(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const result = await signUp(email, password, name);
      if (result.nextStep?.signUpStep === 'CONFIRM_SIGN_UP') {
        setMode('confirm');
      }
    } catch {
      // error is set via context
    }
    setSubmitting(false);
  }

  async function handleConfirm(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await confirmAccount(email, confirmCode);
      await signIn(email, password);
    } catch {
      // error is set via context
    }
    setSubmitting(false);
  }

  function switchMode(newMode) {
    clearError();
    setMode(newMode);
  }

  return (
    <div className="auth-screen">
      <div className="auth-container fade-in">
        <div className="auth-brand">
          <div className="onboarding-logo">
            <span className="logo-icon"><FiZap /></span>
          </div>
          <h1>Welcome to <span className="text-gradient">Recall</span></h1>
        </div>

        {mode === 'login' && (
          <form onSubmit={handleLogin} className="auth-form">
            <h2>Sign In</h2>
            {error && <div className="auth-error">{error}</div>}

            <button
              type="button"
              className="btn btn-apple auth-apple"
              onClick={handleAppleSignIn}
              disabled={submitting}
            >
              <FaApple className="auth-apple-icon" />
              {submitting ? <span className="spinner" style={{ width: 20, height: 20 }} /> : 'Sign in with Apple'}
            </button>

            <div className="auth-divider"><span>or</span></div>

            <div className="auth-field">
              <FiMail className="auth-field-icon" />
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>
            <div className="auth-field">
              <FiLock className="auth-field-icon" />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>
            <button type="submit" className="btn btn-primary btn-lg auth-submit" disabled={submitting}>
              {submitting ? <span className="spinner" style={{ width: 20, height: 20 }} /> : 'Sign In'}
            </button>
            <p className="auth-switch">
              Don't have an account?{' '}
              <button type="button" className="auth-link" onClick={() => switchMode('signup')}>
                Create one
              </button>
            </p>
            {onSkip && (
              <button type="button" className="btn btn-ghost auth-skip" onClick={onSkip}>
                Continue without account
              </button>
            )}
          </form>
        )}

        {mode === 'signup' && (
          <form onSubmit={handleSignUp} className="auth-form">
            <h2>Create Account</h2>
            {error && <div className="auth-error">{error}</div>}

            <button
              type="button"
              className="btn btn-apple auth-apple"
              onClick={handleAppleSignIn}
              disabled={submitting}
            >
              <FaApple className="auth-apple-icon" />
              {submitting ? <span className="spinner" style={{ width: 20, height: 20 }} /> : 'Continue with Apple'}
            </button>

            <div className="auth-divider"><span>or</span></div>

            <div className="auth-field">
              <FiUser className="auth-field-icon" />
              <input
                type="text"
                placeholder="Display name"
                value={name}
                onChange={e => setName(e.target.value)}
                autoComplete="name"
              />
            </div>
            <div className="auth-field">
              <FiMail className="auth-field-icon" />
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>
            <div className="auth-field">
              <FiLock className="auth-field-icon" />
              <input
                type="password"
                placeholder="Password (8+ chars, upper, lower, number)"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>
            <button type="submit" className="btn btn-primary btn-lg auth-submit" disabled={submitting}>
              {submitting ? <span className="spinner" style={{ width: 20, height: 20 }} /> : 'Create Account'}
            </button>
            <p className="auth-switch">
              Already have an account?{' '}
              <button type="button" className="auth-link" onClick={() => switchMode('login')}>
                Sign in
              </button>
            </p>
            {onSkip && (
              <button type="button" className="btn btn-ghost auth-skip" onClick={onSkip}>
                Continue without account
              </button>
            )}
          </form>
        )}

        {mode === 'confirm' && (
          <form onSubmit={handleConfirm} className="auth-form">
            <button type="button" className="btn btn-ghost" onClick={() => switchMode('signup')}>
              <FiArrowLeft /> Back
            </button>
            <h2>Verify Email</h2>
            <p className="auth-subtitle">We sent a code to {email}</p>
            {error && <div className="auth-error">{error}</div>}
            <div className="auth-field">
              <input
                type="text"
                placeholder="Verification code"
                value={confirmCode}
                onChange={e => setConfirmCode(e.target.value)}
                required
                autoComplete="one-time-code"
              />
            </div>
            <button type="submit" className="btn btn-primary btn-lg auth-submit" disabled={submitting}>
              {submitting ? <span className="spinner" style={{ width: 20, height: 20 }} /> : 'Verify & Sign In'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
